import React, { useContext, useMemo, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GlobalContext } from "../../Context/GlobalContext";
import {
  Megaphone, ShoppingCart, FileText, LifeBuoy, Building2,
  Activity, Wallet, TrendingUp, TrendingDown, CheckCircle2, Clock4, AlertTriangle, ArrowRight, Smartphone, Banknote, QrCode
} from "lucide-react";
import Pill from "./components/Pill";
import MiniBarChart from "./components/MiniBarChart";
import WhatsappQrGenerator from "./components/WhatsappQrGenerator";
import useDashboard from "../Services/DashboardServices/useDashboard";

export default function MainDashboard() {
  const {fetchAxisBankBalance}=useDashboard();
  
  const navigate = useNavigate();
  const {  api,statsDash, ordersSeries, AllClients = [] } = useContext(GlobalContext);
  // Axis Bank payout balance
  const [axisBalance, setAxisBalance] = useState(null);
  const [axisLoading, setAxisLoading] = useState(false);
  const [axisError, setAxisError] = useState(null);


const fetchAxisBalance = async () => {
  setAxisLoading(true);
  setAxisError(null);

  const result = await fetchAxisBankBalance();

  if (result) {
    console.log("Axis Bank Balance fetched:", result);
    setAxisBalance(result.balance);
  } else {
    setAxisError(result.message);
    setAxisBalance(null);
  }

  setAxisLoading(false);
};

  useEffect(() => {
    fetchAxisBalance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const derived = useMemo(() => {
    const active = AllClients.filter(c => String(c.us_status) === "1");
    const inactive = AllClients.filter(c => String(c.us_status) !== "1");
    const topBalances = [...AllClients]
      .map(c => ({ ...c, _bal: Number(c.balance || 0) }))
      .sort((a, b) => b._bal - a._bal)
      .slice(0, 8);
    return { activeCount: active.length, inactiveCount: inactive.length, topBalances };
  }, [AllClients]);

  const fmtINR = (n) =>
    new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(Number(n || 0));
  const n = (x) => new Intl.NumberFormat("en-IN").format(Number(x || 0));

  return (
    <>
      {/* KPI Row */}
      {/* KPI Row */}
<div className="mb-4 grid gap-3 md:grid-cols-4">
  <CardKPI 
    title="Successful Payments"
    badge={<Pill color="green" icon={CheckCircle2} w="w-24">Success</Pill>}
    value={fmtINR(statsDash?.success_amount || 0)}
    sub={`${n(statsDash?.success_count || 0)} transactions`}
  />

  <CardKPI 
    title="Pending Payments"
    badge={<Pill color="yellow" icon={Clock4} w="w-24">Pending</Pill>}
    value={fmtINR(statsDash?.pending_amount || 0)}
    sub={`${n(statsDash?.pending_count || 0)} transactions`}
  />

  <CardKPI 
    title="Failed Payments"
    badge={<Pill color="red" icon={AlertTriangle} w="w-24">Failed</Pill>}
    value={fmtINR(statsDash?.failed_amount || 0)}
    sub={`${n(statsDash?.failed_count || 0)} transactions`}
  />

  {/* SWAPPED — Now this is Payout & Admin Info */}
  <CardKPI 
    title="Payout & Admin Info"
    badge={<Pill color="violet" icon={Wallet} w="w-28">Payout</Pill>}
    value={
      axisLoading ? "Fetching…" :
      axisError ? axisError :
      fmtINR(axisBalance || 0)
    }
    sub="Payout Balance"
  />
</div>


      <div className="grid gap-3 lg:grid-cols-3">
        {/* Orders snapshot */}
        <div className="rounded-2xl border bg-white p-3"style={{scrollbarWidth:"none"}}>
          <Header icon={<ShoppingCart size={16} />} title="Orders Snapshot" sub="Received / Dispatched / Completed" style={{scrollbarWidth:"none"}} />
          <MiniBarChart data={ordersSeries || []} style={{scrollbarWidth:"none"}}/>
        </div>

        {/* Clients overview */}
        <div className="rounded-2xl border bg-white p-3">
          <div className="mb-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="grid h-9 w-9 place-items-center rounded-xl bg-[#01C37D]/10 text-[#01C37D]">
                <Building2 size={16} />
              </div>
              <div>
                <div className="text-sm font-semibold">Clients Overview</div>
                <div className="text-xs text-gray-500">{n(AllClients.length)} total clients</div>
              </div>
            </div>
            <div className="flex gap-2">
              <Pill color="green" icon={TrendingUp} w="w-24">{derived.activeCount} Active</Pill>
              <Pill color="red" icon={TrendingDown} w="w-24">{derived.inactiveCount} Inactive</Pill>
            </div>
          </div>

          <div className="max-h-[220px] overflow-auto rounded-xl border" style={{ scrollbarWidth: "none" }}>
            <table className="min-w-full text-left text-sm">
              <thead className="sticky top-0 bg-[#01C37D] text-white">
                <tr>{["Client", "Balance", "Status"].map(h => <th key={h} className="px-3 py-2 font-semibold">{h}</th>)}</tr>
              </thead>
              <tbody>
                {derived.topBalances.map(c => {
                  const active = String(c.us_status) === "1";
                  return (
                    <tr key={c.us_code} className="border-t">
                      <td className="px-3 py-2 truncate">{c.organization_name || c.us_name || "—"}</td>
                      {/* <td className="px-3 py-2">{c.us_code}</td> */}
                      <td className="px-3 py-2 font-semibold">{fmtINR(c._bal)}</td>
                      <td className="px-3 py-2">
                        <Pill color={active ? "green" : "red"} icon={active ? Activity : AlertTriangle} w="w-24">
                          {active ? "Active" : "Inactive"}
                        </Pill>
                      </td>
                    </tr>
                  );
                })}
                {derived.topBalances.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-500">No client data.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Quick routes */}
        <div className="grid grid-rows-3 gap-3">
          <QuickButton onClick={() => navigate("/admin/whatsapp")} iconBg="bg-blue-50 text-blue-600" icon={<Megaphone size={18} />} title="Campaigns" sub="Create, send & track results" />
          <QuickButton onClick={() => navigate("/admin/orders/settlement")} iconBg="bg-emerald-50 text-emerald-600" icon={<FileText size={18} />} title="Settlements & Reports" sub="Analyze, process, export" />
          <QuickButton onClick={() => navigate("/admin/support/")} iconBg="bg-violet-50 text-violet-600" icon={<LifeBuoy size={18} />} title="Support" sub="Tickets, chat & contacts" />
        </div>
      </div>

      {/* Compact actions */}
      <div className="mt-3 grid gap-3 md:grid-cols-3">
        <div className="md:col-span-2">
          <div className="rounded-2xl border bg-white">
            {/* Optional small header */}
            <div className="flex items-center justify-between border-b px-4 py-3">
              <div className="text-sm font-semibold text-gray-900">WhatsApp QR (Generate)</div>
            </div>
            <div className="p-4">
              <WhatsappQrGenerator />
            </div>
          </div>
        </div>

    

       <CardGeneric icon={<Wallet size={16} />} title="Total Wallet">
  <div className="mb-2 rounded-xl bg-gray-50 px-3 py-2">
    <div className="flex items-center justify-between">
      <div>
        <div className="text-[11px] font-medium text-gray-500">Total Wallet Balance</div>
        <div className="text-sm font-semibold text-gray-900">
          {fmtINR(statsDash?.total_wallet || 0)}
        </div>
      </div>

      <div className="grid h-9 w-9 place-items-center rounded-xl bg-violet-50 text-violet-600">
        <Wallet size={16} />
      </div>
    </div>
    <div className="mt-1 text-[10px] text-gray-400">
      Sum across all clients
    </div>
  </div>
</CardGeneric>

      </div>
    </>
  );
}

/* util components */
function Header({ icon, title, sub }) {
  return (
    <div className="mb-2 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <div className="grid h-9 w-9 place-items-center rounded-xl bg-[#01C37D]/10 text-[#01C37D]">{icon}</div>
        <div>
          <div className="text-sm font-semibold">{title}</div>
          {sub && <div className="text-xs text-gray-500">{sub}</div>}
        </div>
      </div>
    </div>
  );
}

function CardKPI({ title, badge, value, sub }) {
  return (
    <div className="rounded-2xl border bg-white p-4">
      <div className="mb-1 flex items-center justify-between text-xs text-gray-500">
        <span className="inline-flex items-center gap-1">{title}</span>
        {badge}
      </div>
      <div className="text-lg font-semibold">{value}</div>
      {sub && <div className="text-xs text-gray-500">{sub}</div>}
    </div>
  );
}

function QuickButton({ onClick, iconBg, icon, title, sub }) {
  return (
    <button onClick={onClick} className="group flex items-center justify-between rounded-2xl border bg-white p-4 text-left hover:shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`grid h-10 w-10 place-items-center rounded-2xl ${iconBg}`}>{icon}</div>
        <div>
          <div className="text-sm font-semibold">{title}</div>
          <div className="text-xs text-gray-500">{sub}</div>
        </div>
      </div>
      <svg className="opacity-0 transition-all group-hover:translate-x-1 group-hover:opacity-100" width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
    </button>
  );
}

function CardGeneric({ icon, title, children }) {
  return (
    <div className="rounded-2xl border bg-white p-3">
      <div className="mb-2 flex items-center gap-2">
        <div className="text-gray-700">{icon}</div>
        <div className="text-sm font-semibold">{title}</div>
      </div>
      {children}
    </div>
  );
}